import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { NavMarker } from 'react-rhumb';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { H7 } from 'foundations-components/transitional/components/H7';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Link } from 'foundations-components/transitional/components/Link';
import { Image } from 'foundations-components/transitional/components/Image';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import { HS_VIDEO_SURFACE, trackHsVideoFailedToRender, trackHsVideoPlayClicked, trackHsVideoRenderedSuccessfully } from 'academy-common-lib/utils/hsVideoMetrics';
import UsageTracker from '../../../utils/UsageTracker';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { getHeroVideo } from './heroVideo';
import { DateIcon } from 'foundations-assets/icons/components/DateIcon';
import { TasksIcon } from 'foundations-assets/icons/components/TasksIcon';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { SocialSlackIcon } from 'foundations-assets/icons/components/SocialSlackIcon';
// @ts-expect-error bender-url module not typed
import heroPlaceholderImage from 'bender-url!../../../../images/ao-hero-img.webp';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RESOURCE_ITEMS = [{
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagLiveWorkshops"
  }),
  icon: /*#__PURE__*/_jsx(DateIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagPlaylists"
  }),
  icon: /*#__PURE__*/_jsx(TasksIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagResources"
  }),
  icon: /*#__PURE__*/_jsx(FileIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagSlackChannel"
  }),
  icon: /*#__PURE__*/_jsx(SocialSlackIcon, {
    size: "600"
  }),
  href: 'https://join.slack.com/t/academy-onboarding/shared_invite/zt-3wzdcgbpi-esCFKK9MnFXNyzAcH8ICkQ?wfoid=30fe609030.1774454921',
  trackingAction: 'onboarding-slack-channel-click'
}];
const ResourceItems = () => {
  return /*#__PURE__*/_jsxs(View, {
    marginBlockStart: "500",
    children: [/*#__PURE__*/_jsx(H7, {
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.academyOnboarding.includes"
      })
    }), /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      columnGap: "600",
      flexWrap: "wrap",
      rowGap: "400",
      children: RESOURCE_ITEMS.map(({
        label,
        icon,
        href,
        trackingAction
      }, index) => /*#__PURE__*/_jsx(MediaObject, {
        align: "center",
        itemLeft: icon,
        spacing: "extra-small",
        children: href ? /*#__PURE__*/_jsx(Link, {
          external: true,
          href: href,
          onClick: () => {
            if (!trackingAction) return;
            UsageTracker.trackStandalone('Learning Center Interaction', {
              action: trackingAction,
              appearsIn: 'academy-onboarding-hero',
              href
            });
          },
          typography: "body-125",
          children: label
        }) : /*#__PURE__*/_jsx(Text, {
          children: label
        })
      }, index))
    })]
  });
};
const HeroBanner = () => {
  const {
    isMobile,
    isTablet,
    isLaptop
  } = useMediaQuery();
  const isStacked = isTablet;
  const heroVideo = getHeroVideo();
  const onHeroHubSpotVideoError = useCallback(e => {
    const detail = e.detail || {};
    const resolvedErrorType = detail.errorType || 'UNKNOWN';
    trackHsVideoFailedToRender(Metrics, {
      errorType: resolvedErrorType,
      surface: HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO
    });
    Raven.captureException(new Error(`HS_VIDEO_FAILED_RENDER_HERO: ${resolvedErrorType}`), {
      tags: {
        hsVideoErrorType: resolvedErrorType
      },
      fingerprint: ['hs-video-failed-render-hero', resolvedErrorType]
    });
  }, []);
  const onHeroHubSpotVideoLoaded = useCallback(() => {
    trackHsVideoRenderedSuccessfully(Metrics, HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO);
  }, []);
  const onHeroHubSpotVideoPlay = useCallback(() => {
    trackHsVideoPlayClicked(Metrics, {
      surface: HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO
    });
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'onboarding-header-video-play',
      appearsIn: 'academy-onboarding-hero'
    });
  }, []);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "ACADEMY_ONBOARDING_HERO_LOADED",
    children: /*#__PURE__*/_jsx(OnboardingSectionLayout, {
      backgroundColor: "fill-surface-default",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: isStacked ? undefined : 'center',
        flexDirection: isStacked ? 'column' : 'row',
        gap: isStacked ? '600' : isLaptop ? '900' : '1500',
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flex: "1",
          flexDirection: "column",
          maxInlineSize: isStacked ? undefined : isLaptop ? '480px' : '629px',
          children: [/*#__PURE__*/_jsx(H2, {
            marginBlockEnd: isMobile ? undefined : '600',
            typography: isMobile ? 'heading-400' : 'heading-600',
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyOnboarding.heading"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyOnboarding.description"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyOnboarding.descriptionCta"
            })
          }), /*#__PURE__*/_jsx(ResourceItems, {})]
        }), !isStacked && /*#__PURE__*/_jsx(View, {
          borderRadius: "400",
          flex: "1",
          maxInlineSize: isLaptop ? '390px' : '627px',
          overflow: "hidden",
          children: heroVideo.hasVideo ? /*#__PURE__*/_jsx(ACVideoPlayer, {
            creatorHubId: heroVideo.creatorHubId,
            onError: onHeroHubSpotVideoError,
            onLoaded: onHeroHubSpotVideoLoaded,
            onPlay: onHeroHubSpotVideoPlay,
            videoId: heroVideo.videoId
          }) : /*#__PURE__*/_jsx(Image, {
            alt: I18n.text('learningCenter.academyOnboarding.heroImageAlt'),
            blockSize: "100%",
            inlineSize: "100%",
            objectFit: "cover",
            src: heroPlaceholderImage
          })
        })]
      })
    })
  });
};
export default HeroBanner;